import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { enderecoDeArquivo } from '@/shared/api/client';
import {
  generated,
  templates,
  type DeliveryFormat,
  type GeneratedDocument,
} from '@/shared/api/resources';
import { useAuth } from '@/shared/auth/useAuth';
import { formatDateTime } from '@/shared/lib/format';
import { AcaoComIcone } from '@/shared/ui/AcaoComIcone';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import { Select } from '@/shared/ui/Select';
import { useToast } from '@/shared/ui/useToast';

import { BaixarDocumento } from '../generation/GenerationPanel';

const formatos: { value: DeliveryFormat; label: string }[] = [
  { value: 'docx', label: 'Word (.docx)' },
  { value: 'pdf', label: 'PDF' },
];

/**
 * "Imprimir minuta" da lista de cotacoes (COT-005) e "Download da minuta" do Resumo
 * (COT-083).
 *
 * No OnPoint os dois geram a minuta na hora (Proposal/GenerateContractDraft). Aqui a
 * minuta sai pelo motor de documento, do modelo publicado da empresa, com os termos
 * habilitados da cotacao, em Word ou em PDF. Gerar de novo substitui a vigente, que
 * continua no historico. Se ja ha minuta vigente, ela pode ser baixada como esta: se
 * imprime a vigente ou gera nova e escolha de quem pede, porque o OnPoint nao deixou
 * ver (docs/ADR/0041-termos-contratuais-da-cotacao.md).
 */
export function DraftPrintDialog({
  operationId,
  title,
  onClose,
}: {
  operationId: string;
  title: string;
  onClose: () => void;
}) {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const podeGerar = can('minutas.minuta.create');
  const [modelo, setModelo] = useState('');
  const [formato, setFormato] = useState<DeliveryFormat>('docx');
  const [texto, setTexto] = useState<GeneratedDocument | null>(null);

  const minutas = useQuery({
    queryKey: ['generated', 'minuta', operationId],
    queryFn: ({ signal }) =>
      generated.listDrafts({ operation_id: operationId, limit: 20 }, signal),
  });
  const modelos = useQuery({
    queryKey: ['templates', { kind: 'minuta', status: 'ativo' }],
    queryFn: ({ signal }) =>
      templates.list({ kind: 'minuta', status: 'ativo', limit: 100 }, signal),
    enabled: podeGerar,
  });

  // A minuta da cotacao vinculada a modalidade (P-MIN-5) ja vem escolhida, como no
  // quadro Minuta do Resumo. Antes a janela listava todos os modelos sem dizer qual era
  // o da modalidade, e o botao ficava travado ate alguem adivinhar (03/11/2026). Sem
  // vinculo, a resposta e 404 e nada vem escolhido.
  const vinculada = useQuery({
    queryKey: ['draft-template', operationId],
    queryFn: ({ signal }) => generated.boundDraftTemplate(operationId, signal),
    enabled: podeGerar,
    retry: false,
  });
  const idDaVinculada = vinculada.data?.id ?? '';

  // So o modelo com versao publicada gera documento.
  const publicados = (modelos.data?.items ?? []).filter(
    (item) => item.current_version > 0,
  );
  const vinculadaPublicada = publicados.some((item) => item.id === idDaVinculada);
  const escolhido =
    modelo ||
    (vinculadaPublicada
      ? idDaVinculada
      : publicados.length === 1
        ? (publicados[0]?.id ?? '')
        : '');
  const vigente = (minutas.data?.items ?? []).find((item) => item.status === 'vigente');

  const gerar = useMutation({
    mutationFn: async () => {
      const documento = await generated.generateDraft(operationId, escolhido);
      if (documento.format !== 'docx') return { documento, url: null };
      if (formato === 'pdf' && !documento.pdf_available) {
        throw new Error(
          'A minuta foi gerada em Word, mas esta instalacao nao converte em PDF. Baixe o Word.',
        );
      }
      const arquivo = await generated.download(documento.id, formato);
      return { documento, url: arquivo.url };
    },
    onSuccess: ({ documento, url }) => {
      toast.success(`Minuta ${documento.number} gerada.`);
      void queryClient.invalidateQueries({ queryKey: ['generated'] });
      void queryClient.invalidateQueries({
        queryKey: ['operations', operationId],
        exact: true,
      });
      if (url) {
        window.open(enderecoDeArquivo(url), '_blank', 'noopener,noreferrer');
        onClose();
      } else {
        // O modelo de texto nao tem arquivo: o texto gerado aparece aqui.
        setTexto(documento);
      }
    },
    onError: (error: Error) => {
      toast.error(error.message);
      void queryClient.invalidateQueries({ queryKey: ['generated'] });
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      description="A minuta sai do modelo publicado da empresa, com o objeto, as condicoes e as clausulas habilitados da cotacao."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Fechar
          </Button>
          {podeGerar ? (
            <Button
              loading={gerar.isPending}
              disabled={escolhido === ''}
              onClick={() => gerar.mutate()}
            >
              Gerar minuta
            </Button>
          ) : null}
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {minutas.isError ? (
          <Alert tone="danger" title="Nao foi possivel carregar as minutas">
            {minutas.error.message}
          </Alert>
        ) : null}

        <section className="flex flex-col gap-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
            Minuta vigente
          </h3>
          {minutas.isPending ? (
            <p className="text-sm text-ink-500">Carregando.</p>
          ) : vigente ? (
            <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-line px-3 py-2">
              <p className="text-sm text-ink-800">
                Minuta {vigente.number}, modelo versao {vigente.template_version},
                gerada por {vigente.generated_label} em{' '}
                {formatDateTime(vigente.generated_at)}.
              </p>
              <div className="flex flex-wrap gap-2">
                {vigente.format === 'docx' ? (
                  <BaixarDocumento documento={vigente} comTexto />
                ) : (
                  <AcaoComIcone
                    acao="verTexto"
                    rotulo="Ver texto"
                    texto
                    onClick={() => setTexto(vigente)}
                  />
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-ink-500">
              A cotacao ainda nao tem minuta gerada.
            </p>
          )}
        </section>

        {podeGerar ? (
          <section className="flex flex-col gap-3">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
              Gerar nova minuta
            </h3>
            {modelos.isPending ? (
              <p className="text-sm text-ink-500">Carregando os modelos.</p>
            ) : publicados.length === 0 ? (
              <Alert tone="info" title="Nenhum modelo de minuta publicado">
                Cadastre o modelo da empresa em Templates e publique a primeira versao.
              </Alert>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                <Select
                  label="Modelo da minuta"
                  placeholder="Escolha o modelo"
                  value={escolhido}
                  options={publicados.map((item) => ({
                    value: item.id,
                    label: `${item.name} (versao ${item.current_version})${
                      item.id === idDaVinculada ? ', modelo da modalidade' : ''
                    }`,
                  }))}
                  hint={
                    vinculadaPublicada
                      ? escolhido === idDaVinculada
                        ? 'Escolhido: o modelo ligado a modalidade da cotacao.'
                        : 'O modelo ligado a modalidade da cotacao esta marcado na lista.'
                      : 'A modalidade da cotacao nao tem modelo ligado: escolha o modelo.'
                  }
                  onChange={(event) => setModelo(event.target.value)}
                />
                <Select
                  label="Formato da minuta"
                  value={formato}
                  options={formatos}
                  onChange={(event) => setFormato(event.target.value as DeliveryFormat)}
                />
              </div>
            )}
          </section>
        ) : null}

        {texto ? (
          <section className="flex flex-col gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-500">
              Texto da minuta {texto.number}
            </h3>
            <pre className="max-h-[40vh] overflow-auto whitespace-pre-wrap rounded bg-ink-50 p-3 text-sm text-ink-800">
              {texto.body}
            </pre>
          </section>
        ) : null}
      </div>
    </Modal>
  );
}
